import React, { useState, useEffect } from 'react';
import { ListGroup, Badge, Alert, Spinner } from 'react-bootstrap';
import { format } from 'date-fns';
import messageService from '../../../services/messageService';
import '../styles/MessageList.css';

const MessageList = ({ type = 'inbox', onMessageSelect, userId }) => {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (userId) {
            loadMessages();
        }
    }, [type, userId]);

    const loadMessages = async () => {
        if (!userId) {
            setError('User ID is required');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const data = type === 'inbox' 
                ? await messageService.getInbox(userId)
                : await messageService.getSentItems(userId);
            setMessages(data);
            setError(null);
        } catch (err) {
            setError('Failed to load messages');
            console.error('Error loading messages:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleMessageClick = async (message) => {
        if (onMessageSelect) {
            // If it's an unread inbox message, mark it as read
            if (type === 'inbox' && !message.isRead) {
                try {
                    await messageService.markAsRead(message.id);
                    // Update the message in the local state
                    setMessages(prevMessages => 
                        prevMessages.map(m => 
                            m.id === message.id ? { ...m, isRead: true } : m
                        )
                    );
                } catch (error) {
                    console.error('Failed to mark message as read:', error);
                }
            }
            onMessageSelect(message);
        }
    };

    if (!userId) {
        return (
            <Alert variant="warning">
                User ID is required to load messages
            </Alert>
        );
    }

    if (loading) {
        return (
            <div className="text-center">
                <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        );
    }

    if (error) {
        return <Alert variant="danger">{error}</Alert>;
    }

    if (messages.length === 0) {
        return (
            <div className="text-center text-muted">
                No messages found
            </div>
        );
    }

    return (
        <ListGroup variant="flush" className="message-list">
            {messages.map(message => (
                <ListGroup.Item
                    key={message.id}
                    action
                    onClick={() => handleMessageClick(message)}
                    className={`message-item ${!message.isRead ? 'unread' : ''}`}
                >
                    <div className="message-preview">
                        <div className="message-header">
                            <span className="message-sender">
                                {type === 'inbox' ? message.senderName : message.recipientName}
                            </span>
                            {!message.isRead && (
                                <Badge bg="primary" pill>New</Badge>
                            )}
                        </div>
                        <div className="message-subject">{message.subject}</div>
                        <div className="message-excerpt">
                            {message.content.replace(/<[^>]*>/g, '').substring(0, 100)}
                        </div>
                        <div className="message-meta">
                            <div className="message-badges">
                                {message.categoryName && (
                                    <Badge bg="light" text="dark">
                                        {message.categoryName}
                                    </Badge>
                                )}
                                {message.attachments && message.attachments.length > 0 && (
                                    <Badge bg="secondary">
                                        <i className="bi bi-paperclip"></i> {message.attachments.length}
                                    </Badge>
                                )}
                            </div>
                            <div className="message-date">
                                {format(new Date(message.createdAt), 'MMM d, h:mm a')}
                            </div>
                        </div>
                    </div>
                </ListGroup.Item>
            ))}
        </ListGroup>
    );
};

export default MessageList; 